import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  DestroyRef,
  effect,
  inject,
  Injector,
  signal,
} from '@angular/core';
import { outputToObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  TellentAssistantConfigService,
  TellentAssistantThreadsFacade,
} from '@core/tellent-assistant-logic';
import {
  FIXED_OVERLAY_LEVEL,
  IconButtonComponent,
  IconColorDirective,
} from '@core/user-interface-legacy';

import { TellentAssistantChatPanelComponent } from '../tellent-assistant-chat-panel/tellent-assistant-chat-panel.component';

@Component({
  selector: 'tellent-assistant',
  standalone: true,
  imports: [IconButtonComponent, IconColorDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './tellent-assistant.component.html',
  styleUrls: ['./tellent-assistant.component.less'],
})
export class TellentAssistantComponent {
  private readonly overlay = inject(Overlay);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly threadsFacade = inject(TellentAssistantThreadsFacade);
  private readonly config = inject(TellentAssistantConfigService);

  protected readonly open = signal<boolean>(false);

  private overlayRef: OverlayRef | null = null;
  private panelRef: ComponentRef<TellentAssistantChatPanelComponent> | null = null;

  constructor() {
    effect(() => (this.open() ? this.attach() : this.detach()));
    effect(() => {
      if (this.open()) {
        this.threadsFacade.loadThreads();
        this.config.loadStarters();
      }
    });
    this.destroyRef.onDestroy(() => this.detach());
  }

  protected toggle(): void {
    this.open.update(v => !v);
  }

  private attach(): void {
    if (this.overlayRef) {
      return;
    }

    const positionStrategy = this.overlay.position().global().right('8px').top('8px');

    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: false,
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      panelClass: 'tellent-assistant-overlay-panel',
    });
    this.overlayRef.hostElement.style.zIndex = `${FIXED_OVERLAY_LEVEL}`;

    const portal = new ComponentPortal(TellentAssistantChatPanelComponent, null, this.injector);
    this.panelRef = this.overlayRef.attach(portal);

    outputToObservable(this.panelRef.instance.close)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.open.set(false));
  }

  private detach(): void {
    if (!this.overlayRef) {
      return;
    }

    this.overlayRef.dispose();
    this.overlayRef = null;
    this.panelRef = null;
  }
}
